import { LitElement, css, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { i18n } from '../i18n/nl';
import { iconCheck } from './icons';

/**
 * `<embrace-rating-card>` — feedback-kaartje na ended-conversation.
 *
 * Twee varianten via `variant`-property:
 *   - `'idle'` — titel + 5 sterren + "Verstuur"-knop. Bezoeker die niet
 *     wil rateren sluit gewoon het gesprek (banner-actie of menu) — dat
 *     wist de session-state inclusief de rating-prompt. Geen losse
 *     "Niet nu"-knop meer, gehouden als één enkele actie.
 *   - `'submitted'` — groene check + bedank-tekst, terminal.
 *
 * Alleen rendering — alle business-rules (mag de bezoeker rateren? was
 * operator attached?) leven in `selectRatingUi(state)`, deze component
 * krijgt alleen de gederiveerde variant + waarden door.
 *
 * Events naar parent:
 *   - `rate-submit` met `{ rating: number }` op klik Verstuur
 */
@customElement('embrace-rating-card')
export class EmbraceRatingCard extends LitElement {
  @property({ type: String })
  variant: 'idle' | 'submitted' = 'idle';

  /** Alleen relevant voor variant='submitted': de waarde voor de bedank-tekst. */
  @property({ type: Number })
  submittedRating = 0;

  /**
   * Ster-N geselecteerd door bezoeker (1..5). 0 = niets geselecteerd nog.
   * Lokale state; reset niet vanzelf bij refresh — dan komt componentje
   * sowieso opnieuw tot leven.
   */
  @state()
  private selected = 0;

  /**
   * Welke ster wordt op dit moment gehoverd (1..5). 0 = geen hover.
   * Tijdens hover lichten alle sterren tot-en-met `hovered` op (preview).
   */
  @state()
  private hovered = 0;

  static override styles = css`
    :host {
      display: block;
    }

    .card {
      background: #fff;
      border: 1px solid #E6E7EA;
      border-radius: 14px;
      padding: 16px 18px;
      box-shadow: 0 1px 2px rgba(12, 28, 71, 0.04);
    }
    .card.success {
      background: #F0F9F4;
      border-color: #B7E4C7;
      display: flex;
      gap: 12px;
      align-items: flex-start;
    }

    .intro {
      font: 400 13.5px/1.45 var(--ec-font-family);
      color: var(--ec-text);
      margin: 0 0 12px;
    }
    .title {
      font: 700 15px/1.3 var(--ec-font-display);
      color: var(--ec-text);
      margin: 0 0 2px;
    }
    .subtitle {
      font: 400 12.5px/1.4 var(--ec-font-family);
      color: var(--ec-text-muted);
      margin: 0 0 12px;
    }

    .stars {
      display: flex;
      gap: 6px;
      margin: 6px 0 8px;
    }
    .star-btn {
      background: none;
      border: 0;
      padding: 2px;
      cursor: pointer;
      border-radius: 4px;
      color: #D8DAE0;
      line-height: 0;
      transition: transform 100ms ease, color 100ms ease;
    }
    .star-btn:hover {
      transform: scale(1.06);
    }
    .star-btn:focus-visible {
      outline: var(--ec-focus-ring-width) solid var(--ec-focus-ring);
      outline-offset: var(--ec-focus-ring-offset);
    }
    .star-btn.lit {
      color: #FFC008; /* Goud — klassieke ster-kleur */
    }
    .star-btn svg {
      width: 32px;
      height: 32px;
    }

    .label {
      font: 600 13px/1.4 var(--ec-font-family);
      color: var(--ec-text);
      min-height: 1.4em; /* Voorkomt layout-shift wanneer label verschijnt */
      margin: 0 0 8px;
    }
    .label.hint {
      font-weight: 400;
      color: var(--ec-text-soft);
    }

    .actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 4px;
    }
    .btn-submit {
      background: var(--ec-primary);
      color: var(--ec-on-primary);
      border: 0;
      padding: 8px 16px;
      font: 600 13px/1.2 var(--ec-font-family);
      border-radius: 8px;
      cursor: pointer;
      transition: background 120ms ease;
    }
    .btn-submit:hover:not(:disabled) {
      background: var(--ec-primary-hover);
    }
    .btn-submit:disabled {
      background: var(--ec-primary-disabled);
      cursor: not-allowed;
    }
    .btn-submit:focus-visible {
      outline: var(--ec-focus-ring-width) solid var(--ec-focus-ring);
      outline-offset: var(--ec-focus-ring-offset);
    }

    /* Success-state */
    .success-icon {
      width: 28px;
      height: 28px;
      flex: 0 0 auto;
      border-radius: 50%;
      background: #2EAA5F;
      color: #fff;
      display: grid;
      place-items: center;
    }
    .success-icon svg {
      width: 18px;
      height: 18px;
    }
    .success-body {
      flex: 1;
    }
    .success-title {
      font: 700 14px/1.3 var(--ec-font-display);
      color: #1B6B3D;
      margin: 0 0 4px;
    }
    .success-text {
      font: 400 13px/1.4 var(--ec-font-family);
      color: #1B6B3D;
      margin: 0;
    }
  `;

  override render() {
    if (this.variant === 'submitted') return this.renderSuccess();
    return this.renderIdle();
  }

  private renderIdle() {
    // De ster-display kijkt naar `hovered` als die >0 is, anders naar
    // `selected`. Op die manier krijgt de bezoeker een live-preview
    // tijdens hover maar valt het na mouse-leave terug op de gekozen
    // waarde — exact het pattern uit de oude widget en alle gangbare
    // 5-star-rating-UIs.
    const display = this.hovered > 0 ? this.hovered : this.selected;
    const labelText =
      this.selected === 0
        ? i18n.rating.hint
        : i18n.rating.labels[this.selected - 1] ?? '';
    const labelClass = this.selected === 0 ? 'label hint' : 'label';
    const canSubmit = this.selected > 0;

    // Roving tabindex: één ster heeft tabindex=0 (de geselecteerde, of
    // anders ster #1). Andere sterren tabindex=-1. Tab-cycle gaat dus
    // langs precies één ster en dan verder; binnen de groep navigeer je
    // met arrow-keys (zie onStarsKeyDown).
    const tabbableStar = this.selected || 1;

    return html`
      <div class="card" part="card">
        <p class="intro">${i18n.rating.botIntro}</p>
        <p class="title">${i18n.rating.title}</p>
        <p class="subtitle">${i18n.rating.subtitle}</p>

        <div
          class="stars"
          role="radiogroup"
          aria-label=${i18n.rating.title}
          @mouseleave=${() => (this.hovered = 0)}
          @keydown=${this.onStarsKeyDown}
        >
          ${[1, 2, 3, 4, 5].map((n) =>
            this.renderStar(n, display, tabbableStar),
          )}
        </div>

        <p class=${labelClass} aria-live="polite">${labelText}</p>

        <div class="actions">
          <button
            type="button"
            class="btn-submit"
            ?disabled=${!canSubmit}
            @click=${this.onSubmit}
          >
            ${i18n.rating.btnSubmit}
          </button>
        </div>
      </div>
    `;
  }

  private renderStar(n: number, display: number, tabbableStar: number) {
    const lit = n <= display;
    return html`
      <button
        type="button"
        class=${`star-btn ${lit ? 'lit' : ''}`}
        role="radio"
        aria-checked=${this.selected === n}
        aria-label=${i18n.rating.starAria(n)}
        tabindex=${n === tabbableStar ? 0 : -1}
        @mouseenter=${() => (this.hovered = n)}
        @click=${() => (this.selected = n)}
      >
        <svg viewBox="0 0 24 24" fill=${lit ? 'currentColor' : 'none'} stroke="currentColor" stroke-width="1.6" stroke-linejoin="round" aria-hidden="true">
          <path d="M12 2.6l2.95 6 6.6.95-4.78 4.66 1.13 6.58L12 17.77l-5.9 3.02 1.13-6.58L2.45 9.55l6.6-.95L12 2.6Z" />
        </svg>
      </button>
    `;
  }

  /**
   * WAI-ARIA radiogroup-keyboard-pattern: pijl-toetsen verplaatsen focus
   * EN committeren de waarde direct (geen "preview"-staat voor keyboard).
   * Home/End spring naar uiteindes. Tab verlaat de groep.
   *
   * Implementatie-detail: na de state-mutatie wachten we op
   * `updateComplete` zodat de ster met de nieuwe tabindex=0 daadwerkelijk
   * focusable is voordat we 'm proberen te focussen.
   */
  private onStarsKeyDown = (e: KeyboardEvent): void => {
    const current = this.selected;
    let next = current;
    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        next = current === 0 ? 1 : Math.max(1, current - 1);
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        next = current === 0 ? 1 : Math.min(5, current + 1);
        break;
      case 'Home':
        next = 1;
        break;
      case 'End':
        next = 5;
        break;
      default:
        return;
    }
    e.preventDefault();
    if (next === current) return;
    this.selected = next;
    void this.updateComplete.then(() => {
      this.shadowRoot
        ?.querySelectorAll<HTMLButtonElement>('.star-btn')
        [next - 1]?.focus();
    });
  };

  private renderSuccess() {
    return html`
      <div class="card success" part="card">
        <span class="success-icon" aria-hidden="true">${iconCheck()}</span>
        <div class="success-body">
          <p class="success-title">${i18n.rating.successTitle}</p>
          <p class="success-text">
            ${i18n.rating.successText(this.submittedRating)}
          </p>
        </div>
      </div>
    `;
  }

  private onSubmit = (): void => {
    if (this.selected <= 0) return;
    this.dispatchEvent(
      new CustomEvent('rate-submit', {
        detail: { rating: this.selected },
        bubbles: true,
        composed: true,
      }),
    );
  };

}

declare global {
  interface HTMLElementTagNameMap {
    'embrace-rating-card': EmbraceRatingCard;
  }
}
